"use client";
import { IResource } from "@gettactic/api";
import React from "react";
import { createPortal } from "react-dom";
import { useMap } from "@/features/map/MapProvider";

export interface MapPulseHighlightsProps {
  /** The rendered map-builder SVG — pulse circles anchor to its
   *  `#interactive-{resourceId}` spot groups. */
  svgRef: React.RefObject<SVGSVGElement | null | undefined>;
}

/**
 * Hover/focus pulse circles for map-builder maps. Extracted from the clone
 * overlay (`MapViewSvgInteractiveZones`), which used to host them: now that
 * builder maps render interactivity natively, only the pulse visuals remain.
 *
 * The two template groups render hidden (portaled into the base SVG so the
 * SVG namespace is right for cloning). On a highlight/focus event a CLONE
 * of the template is inserted inside the target spot's group, directly
 * before its dot circle — so the pulse paints above the desk rect but
 * underneath the dot, exactly like the old overlay's stacking. Because the
 * clone inherits the spot's transform chain, positioning is just the dot's
 * local cx/cy. For area-only spots (hidden dot) the state area brightens
 * instead of any pulse.
 */
export const MapPulseHighlights: React.FC<MapPulseHighlightsProps> = ({
  svgRef
}) => {
  const { events, zonesInfo } = useMap();
  // useId output contains characters (e.g. ":") that break url(#...) SVG
  // references — keep only url-fragment-safe ones.
  const gradientIdBase = React.useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const highlightGradientId = `pulse-highlight-${gradientIdBase}`;
  const focusGradientId = `pulse-focus-${gradientIdBase}`;

  const [hoveringCircle, setHoveringCircle] =
    React.useState<SVGGElement | null>(null);
  const [focusCircle, setFocusCircle] = React.useState<SVGGElement | null>(
    null
  );
  const [pulseLayer, setPulseLayer] = React.useState<SVGGElement | null>(null);

  // (Re)locate the portal slot whenever a map finishes loading — zonesInfo
  // repopulates after the SVG mounts, including on floor switches.
  React.useEffect(() => {
    setPulseLayer(
      svgRef.current?.querySelector<SVGGElement>("#pulse-layer") ?? null
    );
  }, [zonesInfo.mapInfo, svgRef]);

  // Live pulse clone per template (one for hover, one for focus).
  const pulseCloneByTemplate = React.useRef(new Map<SVGElement, SVGElement>());
  // For spots configured area-only (hidden dot), highlighting brightens the
  // spot's state area instead. Tracked per template so moving/clearing the
  // highlight restores the previous area.
  const areaByTemplate = React.useRef(new Map<SVGElement, SVGElement>());
  // Clones + brightened areas of the multi-resource colleague highlight.
  const userHighlightClones = React.useRef<SVGElement[]>([]);
  const userHighlightAreas = React.useRef<SVGElement[]>([]);

  const clearTemplateHighlight = React.useCallback((template: SVGElement) => {
    const clone = pulseCloneByTemplate.current.get(template);
    if (clone) {
      clone.remove();
      pulseCloneByTemplate.current.delete(template);
    }
    const area = areaByTemplate.current.get(template);
    if (area) {
      area.style.fillOpacity = "";
      areaByTemplate.current.delete(template);
    }
  }, []);

  /** Find the (visible) dot circle of a resource's spot in the base SVG. */
  const findSpotCircle = React.useCallback(
    (resource: IResource): SVGCircleElement | undefined => {
      const svg = svgRef.current;
      if (!svg) return undefined;
      return Array.from(
        svg.querySelectorAll(`#interactive-${resource.id} circle`) ?? []
      ).find((candidate) => {
        const style = window.getComputedStyle(candidate);
        return style.display !== "none";
      }) as SVGCircleElement | undefined;
    },
    [svgRef]
  );

  /**
   * Insert a pulse clone under the spot's dot (above the desk rect), or
   * brighten the state area for dot-less spots. Returns what it created so
   * callers can track it for cleanup.
   */
  const applyPulseToResource = React.useCallback(
    (
      template: SVGElement,
      resource: IResource
    ): { clone?: SVGElement; area?: SVGElement } => {
      const circle = findSpotCircle(resource);
      if (!circle) return {};

      if (circle.dataset.dotHidden === "true") {
        const area = circle
          .closest(".spot-shape-container")
          ?.querySelector<SVGElement>(".spot-state-area");
        if (!area) return {};
        area.style.fillOpacity = "0.5";
        return { area };
      }

      const cx = Number.parseFloat(String(circle.getAttribute("cx") || 0));
      const cy = Number.parseFloat(String(circle.getAttribute("cy") || 0));

      const clone = template.cloneNode(true) as SVGElement;
      clone.setAttribute("style", "display:block");
      clone.setAttribute("transform", `translate(${cx - 37}, ${cy - 37})`);
      // Before the circle in its own group: above the spot body (desk
      // rect, label), below the dot and profile photo.
      circle.parentNode?.insertBefore(clone, circle);
      return { clone };
    },
    [findSpotCircle]
  );

  const moveCircleToResource = React.useCallback(
    (template: SVGElement, resource: IResource | null) => {
      if (!template) return;
      clearTemplateHighlight(template);
      if (!resource) return;

      const { clone, area } = applyPulseToResource(template, resource);
      if (clone) pulseCloneByTemplate.current.set(template, clone);
      if (area) areaByTemplate.current.set(template, area);
    },
    [clearTemplateHighlight, applyPulseToResource]
  );

  React.useEffect(() => {
    const onHighlightUserResource = (resources: IResource[] | null) => {
      if (!hoveringCircle) return;

      userHighlightClones.current.forEach((clone) => {
        clone.remove();
      });
      userHighlightClones.current = [];
      userHighlightAreas.current.forEach((area) => {
        area.style.fillOpacity = "";
      });
      userHighlightAreas.current = [];

      resources?.forEach((resource) => {
        const { clone, area } = applyPulseToResource(hoveringCircle, resource);
        if (clone) {
          clone.classList.add("user-highlight-circle");
          userHighlightClones.current.push(clone);
        }
        if (area) userHighlightAreas.current.push(area);
      });
    };

    events.register("onHighlightUserResource", onHighlightUserResource);

    return () => {
      events.unregister("onHighlightUserResource", onHighlightUserResource);
    };
  }, [hoveringCircle, events, applyPulseToResource]);

  React.useEffect(() => {
    const onHighlightResource = (resource: IResource | null) => {
      if (!hoveringCircle) return;
      moveCircleToResource(hoveringCircle, resource);
    };
    events.register("onHighlightResource", onHighlightResource);

    return () => {
      events.unregister("onHighlightResource", onHighlightResource);
    };
  }, [hoveringCircle, events, moveCircleToResource]);

  React.useEffect(() => {
    const onFocusResource = (resource: IResource | null) => {
      if (!focusCircle) return;
      moveCircleToResource(focusCircle, resource);
    };
    events.register("onFocusResource", onFocusResource);

    return () => {
      events.unregister("onFocusResource", onFocusResource);
    };
  }, [focusCircle, events, moveCircleToResource]);

  if (!pulseLayer) {
    return null;
  }

  return createPortal(
    <>
      <g
        style={{ display: "none" }}
        ref={setHoveringCircle}
        className="hh-circle map-pulse-highlight absolute top-0 left-0 hidden overflow-hidden"
      >
        <circle
          cx="37.2041"
          cy="37"
          r="37"
          fill={`url(#${highlightGradientId})`}
        />
        <rect
          x="21.21"
          y="21"
          width="32"
          height="32"
          rx="16"
          stroke="#4F48F7"
          strokeWidth="4"
        />
        <defs>
          <radialGradient
            id={highlightGradientId}
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(37.2041 37) rotate(90) scale(37)"
          >
            <stop offset="0.655" stopColor="#656CFF" stopOpacity="0.4" />
            <stop offset="1" stopColor="#656CFF" />
          </radialGradient>
        </defs>
      </g>
      <g
        ref={setFocusCircle}
        style={{ display: "none" }}
        className="map-pulse hh-circle absolute top-0 left-0 hidden"
      >
        <circle cx="37.2041" cy="37" r="37" fill={`url(#${focusGradientId})`} />
        <rect
          x="21.21"
          y="21"
          width="32"
          height="32"
          rx="16"
          stroke="#4F48F7"
          strokeWidth="4"
        />
        <defs>
          <radialGradient
            id={focusGradientId}
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(37.2041 37) rotate(90) scale(37)"
          >
            <stop offset="0.655" stopColor="#656CFF" stopOpacity="0.4" />
            <stop offset="1" stopColor="#656CFF" />
          </radialGradient>
        </defs>
      </g>
    </>,
    pulseLayer
  );
};
